import {
  CreateOrUpdateLooselyRelatedAuthor,
  createOrUpdateLooselyRelatedAuthorSchema,
  looselyRealatedAuthorToCreateOrUpdate,
  LooselyRelatedAuthor,
  useCreateLooselyRelatedAuthor,
  useDeleteLooselyRelatedAuthor,
  useGetLooselyRelatedAuthors,
  useUpdateLooselyRelatedAuthor,
} from "@/api/exhibition/exhibition";
import * as AF from "@/components/form-items/AbstractForm";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { DataTable } from "@/components/ui/datatable";
import { Dialog, DialogClose, DialogHeader } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

type CreateOrUpdateLooselyRelatedAuthorModalProps = {
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  looselyRelatedAuthor?: LooselyRelatedAuthor;
  exhibitionId: string;
};

export const CreateOrEditLooselyRelatedAuthorModal = ({
  onOpenChange,
  open,
  looselyRelatedAuthor: looselyRelatedAuthor,
  exhibitionId,
}: CreateOrUpdateLooselyRelatedAuthorModalProps) => {
  const isEdit = looselyRelatedAuthor !== undefined;

  const updater = useUpdateLooselyRelatedAuthor();
  const creator = useCreateLooselyRelatedAuthor();
  const deleter = useDeleteLooselyRelatedAuthor();

  const { t } = useTranslation();

  const form = useForm<CreateOrUpdateLooselyRelatedAuthor>({
    defaultValues: isEdit
      ? looselyRealatedAuthorToCreateOrUpdate(looselyRelatedAuthor)
      : {
          fallbackLabel: "",
        },

    resolver: zodResolver(createOrUpdateLooselyRelatedAuthorSchema),
  });

  useEffect(() => {
    if (looselyRelatedAuthor) {
      form.reset(looselyRealatedAuthorToCreateOrUpdate(looselyRelatedAuthor));
    }
  }, [looselyRelatedAuthor, form]);

  function onSubmit(values: CreateOrUpdateLooselyRelatedAuthor) {
    if (isEdit) {
      updater.mutate(
        {
          exhibitionId,
          data: values,
          looselyRelatedAuthorId: looselyRelatedAuthor.id,
        },
        { onSuccess: () => onOpenChange?.(false) },
      );
      return;
    }

    creator.mutate(
      {
        data: values,
        exhibitionId,
      },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange?.(false);
        },
      },
    );
  }
  function onDelete() {
    if (!isEdit) return;
    deleter.mutate(
      { exhibitionId, looselyRelatedAuthorId: looselyRelatedAuthor.id },
      {
        onSuccess: () => onOpenChange?.(false),
      },
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>
                {isEdit
                  ? t("autresArtistesTitreModaleEditer")
                  : t("autresArtistesTitreModaleAjouter")}
              </CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text
                name="fallbackLabel"
                label={t("autresArtistesModaleNomLabel")}
              />
              {isEdit && (
                <DeleteButton
                  variant={"link"}
                  className="text-red-600 w-fit m-auto"
                  iconProps={{ className: "stroke-red-600" }}
                  onClick={onDelete}
                  type="button"
                >
                  {t("autresArtistesModaleSupprimer")}
                </DeleteButton>
              )}
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <CancelButton className="flex-1" />
              </DialogClose>
              {isEdit ? (
                <UpdateButton type="submit" className="flex-1" />
              ) : (
                <SaveButton
                  type="submit"
                  disabled={!form.formState.isValid}
                  className="flex-1"
                />
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

export const OtherAuthorsTable = ({
  exhibitionId,
}: {
  exhibitionId: string;
}) => {
  const { t } = useTranslation();

  const { data = [] } = useGetLooselyRelatedAuthors(exhibitionId);

  const looselyRelatedAuthorToEdit = useRef<LooselyRelatedAuthor>();

  const [openEditForm, setOpenEditForm] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);

  const columns = useMemo<ColumnDef<LooselyRelatedAuthor>[]>(
    () => [
      {
        id: "name",
        header: t("autresArtistesArtiste"),
        cell: ({ row }) => (
          <>
            {"fallbackLabel" in row.original
              ? row.original.fallbackLabel
              : `${row.original.author.artistesAuteursDescriptionNom} ${row.original.author.artistesAuteursDescriptionPrenom}`}
          </>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-2">
      <CreateOrEditLooselyRelatedAuthorModal
        open={openCreateForm}
        onOpenChange={setOpenCreateForm}
        exhibitionId={exhibitionId}
      />
      <CreateOrEditLooselyRelatedAuthorModal
        open={openEditForm}
        onOpenChange={setOpenEditForm}
        exhibitionId={exhibitionId}
        looselyRelatedAuthor={looselyRelatedAuthorToEdit.current}
      />
      <div className="flex justify-end">
        <AddButton type="button" onClick={() => setOpenCreateForm(true)} />
      </div>
      <DataTable
        columns={columns}
        data={data}
        onRowClick={(row) => {
          looselyRelatedAuthorToEdit.current = row.original;
          setOpenEditForm(true);
        }}
      />
    </div>
  );
};
